import React from 'react'
import { labelClass, labelStyle } from '@/lib/styleKit'

/** The bundle a `Field` hands its control. Spread it — `<input {...f} />`. */
export interface FieldAria {
  id: string
  'aria-describedby': string | undefined
  'aria-invalid': true | undefined
}

interface FieldBaseProps {
  label: React.ReactNode
  /** Explanatory text under the control. Wired via `aria-describedby`. */
  hint?: React.ReactNode
  /** Error text under the control. Wired via `aria-describedby` + `aria-invalid`, announced via `role="alert"`. */
  error?: string
  /** Appends a red asterisk to the label. */
  required?: boolean
  className?: string
}

interface FieldControlProps extends FieldBaseProps {
  as?: 'field'
  /**
   * Render the control, spreading the supplied bundle onto it. A render prop
   * rather than `cloneElement` on purpose: injecting props into an unknown child
   * silently breaks on any control that does not forward them.
   */
  children: (aria: FieldAria) => React.ReactNode
}

interface FieldGroupProps extends FieldBaseProps {
  /**
   * For a composite control that has no single focusable node to point at — a
   * segmented button row, a pill group, a picker that takes no `id`. The label
   * becomes a `<span>` and the wrapper a `role="group"` named by it, which is
   * the correct mapping; a dangling `htmlFor` would be worse than none.
   */
  as: 'group'
  children: React.ReactNode
}

export type FieldProps = FieldControlProps | FieldGroupProps

/**
 * The labelled form field (§56). Owns the label, the generated id, the
 * `htmlFor` association and the `aria-describedby` / `aria-invalid` wiring, so
 * no call site has to remember them.
 *
 * Before this component the 24 workflow config panels carried ~90 fields with
 * exactly 3 `htmlFor` between them — every other label named nothing, and
 * clicking a label did not focus its control.
 *
 * ```tsx
 * <Field label="Duration">
 *   {(f) => <input {...f} type="number" className={inputClass} style={inputStyle} />}
 * </Field>
 *
 * <Field label="Unit">
 *   {(f) => <Select {...f} value={unit} onChange={setUnit} options={UNITS} />}
 * </Field>
 *
 * <Field label="Wait mode" as="group">
 *   <div className="flex …">{modes.map(…)}</div>
 * </Field>
 * ```
 */
export const Field: React.FC<FieldProps> = ({
  label,
  hint,
  error,
  required,
  className,
  ...rest
}) => {
  const uid = React.useId()
  const controlId = `${uid}control`
  const labelId = `${uid}label`
  const hintId = `${uid}hint`
  const errorId = `${uid}error`

  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined

  const labelBody = (
    <>
      {label}
      {required && <span className="text-red-500 dark:text-red-400"> *</span>}
    </>
  )

  const below = (
    <>
      {hint && (
        <p
          id={hintId}
          className="mt-1.5 text-muted-foreground dark:text-[#8a8f98]"
          style={{ fontSize: '12px', lineHeight: 1.55, letterSpacing: '-0.1px' }}
        >
          {hint}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-1.5 text-red-600 dark:text-red-400"
          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
        >
          {error}
        </p>
      )}
    </>
  )

  if (rest.as === 'group') {
    return (
      <div
        className={className}
        role="group"
        aria-labelledby={labelId}
        aria-describedby={describedBy}
      >
        <span id={labelId} className={labelClass} style={labelStyle}>
          {labelBody}
        </span>
        {rest.children}
        {below}
      </div>
    )
  }

  return (
    <div className={className}>
      <label htmlFor={controlId} className={labelClass} style={labelStyle}>
        {labelBody}
      </label>
      {rest.children({
        id: controlId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })}
      {below}
    </div>
  )
}

export default Field
